import { useState, useEffect, useRef } from "react";
import { SYMBOL_TO_ID, useLiveMarket } from "../dashboard/UseLivemarket";
import { useNavigate } from "react-router-dom";

const FUTURES_SYMBOLS = [
  "BTCUSDT",
  "XRPUSDT",
  "DOGEUSDT",
  "LINKUSDT",
  "SHIBUSDT",
  "ETHUSDT",
  "DASHUSDT",
  "BCHUSDT",
  "FILUSDT",
  "LTCUSDT",
  "YFIUSDT",
  "ADAUSDT",
  "DOTUSDT",
  "ZECUSDT",
  "TRXUSDT",
];

const FOREX_SYMBOLS = [
  "EURUSDT",
  "GBPUSDT",
  "AUDUSDT",
  "TRYUSDT",
  "BRLUSDT",
  "RUBUSDT",
  "IDRUSDT",
  "UAHUSDT",
];

const formatPrice = (price) => {
  const n = Number(price);
  if (!n) return "0";
  if (n < 0.000001) return n.toFixed(8);
  if (n < 0.001) return n.toFixed(7);
  if (n < 1) return n.toFixed(5);
  if (n > 10000)
    return n.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  if (n > 100) return n.toFixed(3);
  return n.toFixed(4);
};

const formatVolume = (vol) => {
  const n = Number(vol);
  if (n >= 1e9) return (n / 1e9).toFixed(2) + "B";
  if (n >= 1e6) return (n / 1e6).toFixed(2) + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(2) + "K";
  return n.toFixed(2);
};

const COIN_LOGO = (baseSymbol) => {
  const symKey = `${baseSymbol.toUpperCase()}USDT`;
  const id = SYMBOL_TO_ID[symKey];

  if (id) {
    return `https://assets.coincap.io/assets/icons/${id}@2x.png`;
  }

  return `https://cryptoicons.org/api/color/${baseSymbol.toLowerCase()}/64`;
};

function CoinAvatar({ symbol }) {
  const [failed, setFailed] = useState(false);

  // Color based on symbol for consistent letter avatars
  const colors = [
    "#0e7490",
    "#065f46",
    "#7c3aed",
    "#b45309",
    "#be123c",
    "#1d4ed8",
    "#15803d",
    "#c2410c",
    "#6d28d9",
    "#0369a1",
  ];
  const colorIndex = symbol.charCodeAt(0) % colors.length;
  const bgColor = colors[colorIndex];

  if (failed) {
    return (
      <div
        className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 border border-cyan-900/30 text-white font-bold text-sm"
        style={{ backgroundColor: bgColor }}
      >
        {symbol[0]}
      </div>
    );
  }

  return (
    <img
      src={COIN_LOGO(symbol)}
      alt={symbol}
      className="w-9 h-9 rounded-full bg-[#1a2030] border border-cyan-900/30 
      object-contain flex-shrink-0 max-[350px]:w-7 max-[350px]:h-7"
      onError={() => setFailed(true)}
    />
  );
}

function MarketRow({ coin }) {
  const change = Number(coin.priceChangePercent ?? 0);
  const positive = change >= 0;
  const base = coin.symbol.replace("USDT", "");
  const baseSymbol = coin.symbol?.includes("/")
    ? coin.symbol.split("/")[0]
    : (coin.symbol?.replace("USDT", "") ?? "?");

  const [flash, setFlash] = useState(null);
  const prevRef = useRef(null);
  useEffect(() => {
    if (!coin.lastPrice) return;
    if (prevRef.current && coin.lastPrice !== prevRef.current) {
      setFlash(
        Number(coin.lastPrice) > Number(prevRef.current) ? "up" : "down",
      );
      const t = setTimeout(() => setFlash(null), 400);
      prevRef.current = coin.lastPrice;
      return () => clearTimeout(t);
    }
    prevRef.current = coin.lastPrice;
  }, [coin.lastPrice]);

  const priceColor =
    flash === "up" ? "#18d05c" : flash === "down" ? "#ff5252" : "#ffffff";

  return (
    <div className="flex items-center justify-between py-3.5 border-b border-white/5 last:border-0 cursor-pointer hover:bg-white/[0.03] transition-colors">
      <div className="pr-2">
        <CoinAvatar symbol={baseSymbol} />
      </div>
      <div className="flex-1">
        <p className="text-white font-bold text-[15px] leading-tight max-[350px]:text-[13px]">
          {base}{" "}
          <span className="text-gray-500 font-normal text-[13px] max-[350px]:text-[11px]">/ USDT</span>
        </p>
        <p className="text-gray-500 text-[11px] mt-0.5">
          VOL:{" "}
          <span className="text-gray-400">
            {formatVolume(coin.quoteVolume)}
          </span>
        </p>
      </div>

      <div className="flex-1 text-center">
        <p
          className="font-bold text-[15px] tabular-nums leading-tight transition-colors duration-200 max-[350px]:text-[13px]"
          style={{ color: priceColor }}
        >
          {formatPrice(coin.lastPrice)}
        </p>
        <p className="text-gray-500 text-[11px] mt-0.5 tabular-nums max-[350px]:text-[9px]">
          $ {formatPrice(coin.lastPrice)}
        </p>
      </div>

      <div className="flex-1 flex justify-end">
        <span
          className="inline-block text-white font-bold text-[13px] tabular-nums rounded-lg px-4 py-2.5 min-w-[80px] text-center max-[350px]:text-[11px] max-[350px]:min-w-[60px] max-[350px]:px-3"
          style={{ backgroundColor: positive ? "#00b578" : "#ff5252" }}
        >
          {positive ? "+" : ""}
          {change.toFixed(2)}%
        </span>
      </div>
    </div>
  );
}

function LiveBadge({ mode }) {
  const cfg =
    mode === "ws"
      ? {
          dot: "bg-cyan-400",
          text: "text-cyan-400",
          label: "LIVE",
          glow: "0 0 6px #00e5ff",
        }
      : mode === "polling"
        ? {
            dot: "bg-green-400",
            text: "text-green-400",
            label: "LIVE ~2s",
            glow: "0 0 5px #4ade80",
          }
        : {
            dot: "bg-amber-400",
            text: "text-amber-400",
            label: "Connecting…",
            glow: "none",
          };
  return (
    <div className="flex items-center gap-1.5">
      <span
        className={`w-1.5 h-1.5 rounded-full animate-pulse ${cfg.dot}`}
        style={{ boxShadow: cfg.glow }}
      />
      <span className={`text-[10px] font-bold tracking-widest ${cfg.text}`}>
        {cfg.label}
      </span>
    </div>
  );
}

function TableHeader({ sortBy, onSort }) {
  return (
    <div className="flex items-center justify-between py-2 mb-1 border-b border-white/10">
      <span className="flex-1 text-gray-500 text-[12px] font-medium">Pair</span>
      <span className="flex-1 text-center text-gray-500 text-[12px] font-medium">
        Price
      </span>
      <div
        className="flex-1 flex justify-end items-center gap-1 cursor-pointer select-none"
        onClick={() =>
          onSort(sortBy === "change_desc" ? "change_asc" : "change_desc")
        }
      >
        <span className="text-gray-500 text-[12px] font-medium">Change</span>
        <div className="flex flex-col gap-px ml-0.5">
          <svg
            className={`w-2 h-2 ${sortBy === "change_desc" ? "text-cyan-400" : "text-gray-600"}`}
            viewBox="0 0 8 5"
            fill="currentColor"
          >
            <path d="M4 0L8 5H0z" />
          </svg>
          <svg
            className={`w-2 h-2 ${sortBy === "change_asc" ? "text-cyan-400" : "text-gray-600"}`}
            viewBox="0 0 8 5"
            fill="currentColor"
          >
            <path d="M4 5L0 0h8z" />
          </svg>
        </div>
      </div>
    </div>
  );
}

export default function Markets() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("change_desc");
  const { coins, mode } = useLiveMarket();

  const symbols = tab === 0 ? FUTURES_SYMBOLS : FOREX_SYMBOLS;

  const rows = symbols
    .map((s) => coins[s])
    .filter((c) => {
      if (!c?.lastPrice) return false;
      if (!search) return true;
      return c.symbol.toLowerCase().includes(search.toLowerCase());
    });

  const sorted =
    sortBy === "change_desc"
      ? [...rows].sort(
          (a, b) => Number(b.priceChangePercent) - Number(a.priceChangePercent),
        )
      : [...rows].sort(
          (a, b) => Number(a.priceChangePercent) - Number(b.priceChangePercent),
        );

  return (
    <div className="min-h-screen bg-[#0d1117] pb-24">
      <div className="flex items-center justify-center gap-6 mb-3 pt-3 ">
        {["Crypto"].map((label, i) => (
          <button
            key={label}
            onClick={() => {
              setTab(i);
              setSearch("");
            }}
            className={`text-[17px] font-bold pb-1 transition-all duration-300 font-mono tracking-wider ${
              tab === i
                ? "text-transparent bg-clip-text bg-gradient-to-r from-[#00A0FF] via-[#00C8FF] to-[#66E0FF] drop-shadow-[0_0_8px_rgba(0,160,255,0.45)] border-b-2 border-[#00A0FF]"
                : "text-gray-500 hover:text-[#00A0FF]"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Search bar */}
      <div className="relative mx-4 mb-2">
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 w-4 h-4"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          viewBox="0 0 24 24"
        >
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
        <input
          type="text"
          placeholder="Search pair..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full bg-[#1a2030] text-white text-sm pl-9 pr-4 py-2.5 rounded-xl border border-cyan-900/30 placeholder-gray-600 outline-none focus:border-cyan-400/40"
        />
      </div>

      {/* Live badge — left side, just below search */}
      <div className="px-4 mb-2">
        <LiveBadge mode={mode} />
      </div>

      {/* Table header */}
      <div className="px-4">
        <TableHeader sortBy={sortBy} onSort={setSortBy} />
      </div>

      {/* Coin rows */}
      <div className="px-4">
        {sorted.length === 0 ? (
          <div className="py-12 text-center text-gray-600 text-sm">
            {Object.keys(coins).length === 0
              ? "Loading market data…"
              : "No pairs found"}
          </div>
        ) : (
          sorted.map((coin) => (
            <div
              key={coin.symbol}
              onClick={() => navigate(`/binary/${coin.symbol}`)}
            >
              <MarketRow coin={coin} />
            </div>
          ))
        )}
      </div>
    </div>
  );
}
